import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useCalculateDeliveryFeesMutation } from "@/store/api/orderApi";
import { Branch } from "@/types/branch";

interface Address {
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  postal_code?: string;
}

interface UseDeliveryFeeProps {
  branch: Branch | undefined;
  deliveryAddress: Address;
  onSuccess?: () => void;
}

export function useDeliveryFee({
  branch,
  deliveryAddress,
  onSuccess,
}: UseDeliveryFeeProps) {
  const [deliveryFee, setDeliveryFee] = useState("");
  const [deliveryFeeCalculated, setDeliveryFeeCalculated] = useState(false);
  const [deliveryFeeError, setDeliveryFeeError] = useState("");
  const [deliveryAddressId, setDeliveryAddressId] = useState<
    number | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  const [calculateDeliveryFees] = useCalculateDeliveryFeesMutation();

  const calculateDeliveryFee = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!branch) return;

      const requestBody = {
        branch_id: branch.id,
        origin_address: `${branch?.address?.street}, ${branch?.address?.city}, ${branch?.address?.province}, ${branch?.address?.country}, ${branch?.address?.postal_code}`,
        address: deliveryAddress?.street,
        city: deliveryAddress?.city,
        region: deliveryAddress?.province,
        country: deliveryAddress?.country,
        postal: deliveryAddress?.postal_code,
      };

      setLoading(true);
      try {
        const response = await calculateDeliveryFees(requestBody).unwrap();
        const fees = response.fees.toString();
        setDeliveryAddressId(response.address_id);
        setDeliveryFee(fees);
        setDeliveryFeeCalculated(true);
        setDeliveryFeeError("");
        onSuccess?.();
        return { success: true, fees };
      } catch (error: any) {
        console.error("Error calculating delivery fees:", error);
        const errorMessage =
          error?.data?.error || "Failed to calculate delivery fees";
        toast.error(errorMessage);
        setDeliveryFeeError(errorMessage);
        setDeliveryFeeCalculated(false);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [branch, deliveryAddress, calculateDeliveryFees, onSuccess],
  );

  const resetDeliveryFee = useCallback(() => {
    setDeliveryFee("");
    setDeliveryFeeCalculated(false);
    setDeliveryFeeError("");
    setDeliveryAddressId(undefined);
  }, []);

  return {
    deliveryFee,
    deliveryFeeCalculated,
    deliveryFeeError,
    deliveryAddressId,
    deliveryLoading: loading,
    setDeliveryFee,
    calculateDeliveryFee,
    resetDeliveryFee,
  };
}
